import { parseServerTimestamp } from '@/utils/time'
import { formatCountdown, formatEventDay, formatEventTime, secondsUntil } from '../time'
import type { RoomState } from '../types'
import { presentersShown } from './presenters'
import { SpeakerAvatar } from './SpeakerAvatar'
import type { SlateSpeaker } from './ViewerSlate'

/** Past this many the chips would crowd the countdown, so the last place says how many more there are. */
const PRESENTER_CHIPS_MAX = 3

/** Who is presenting, as chips beside the countdown: photo, name, then the title and the company on lines of their own. */
function PresenterChips({ speakers }: { speakers: SlateSpeaker[] }) {
    const { shown, more } = presentersShown(speakers, PRESENTER_CHIPS_MAX)
    return (
        <ul class="v-wait-pres" aria-label="Presenting">
            {shown.map((speaker, index) => (
                <li class="v-wait-chip" key={`${index}:${speaker.Name}`}>
                    <SpeakerAvatar name={speaker.Name} photoUrl={speaker.PhotoUrl ?? null} className="v-wait-photo" />
                    <span class="v-wait-chip-text">
                        <span class="v-wait-chip-name">{speaker.Name}</span>
                        {speaker.Title && <span class="v-wait-chip-role">{speaker.Title}</span>}
                        {speaker.Company && <span class="v-wait-chip-role">{speaker.Company}</span>}
                    </span>
                </li>
            ))}
            {more > 0 && <li class="v-wait-more">+{more} more</li>}
        </ul>
    )
}

interface ViewerWaitingBandProps {
    room: Pick<RoomState, 'StartUtc' | 'TimeZone'>
    speakers: SlateSpeaker[]
    now: number
}

/**
 * The band under whatever shows while viewers wait (ORB-1118), the waiting video or the image set instead: "Starting
 * soon", the countdown and the day and time, with the presenters beside them.
 */
export function ViewerWaitingBand({ room, speakers, now }: ViewerWaitingBandProps) {
    const startMs = parseServerTimestamp(room.StartUtc)
    const count = secondsUntil(startMs, now)
    const start = startMs === null ? null : new Date(startMs)
    const when = start ? `${formatEventDay(start, room.TimeZone)} · ${formatEventTime(start, room.TimeZone)}` : null

    return (
        <div class="v-wait-band">
            <div class="v-wait-clock">
                <p class="v-wait-eyebrow">Starting soon</p>
                {count !== null && <p class="v-wait-count" role="timer">{formatCountdown(count)}</p>}
                {when && <p class="v-wait-when">{when}</p>}
            </div>
            {speakers.length > 0 && <PresenterChips speakers={speakers} />}
        </div>
    )
}
